import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthenticatedFetch } from '../utils/api';
import { shopifyAppBridge } from '../App';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { triggerDownload } from '../utils/imageUtils';

const POLL_INTERVAL_MS = 2000;
const MU_KEY = 'mu_session_v1';

/**
 * Manages the full manual-upload lifecycle:
 *   idle → uploading → optimizing → done
 *
 * Exposes grouped state and stable handler callbacks.
 *
 * @returns {{
 *   session: object,        // { phase, sessionId, data, uploadError, aborting }
 *   downloading: object,    // { file: safeName|null, zip: bool }
 *   recentsKey: number,     // bump to force RecentUploads refresh
 *   startUpload: Function,
 *   handleAbort: Function,
 *   handleDownloadFile: Function,
 *   handleDownloadZip: Function,
 *   handleReset: Function,
 * }}
 */
function useManualUpload() {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const authenticatedFetchRef  = useRef(authenticatedFetch);
  useEffect(() => { authenticatedFetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  // Keep a ref to the latest session so async handlers always read fresh values
  const sessionRef = useRef(null);

  const [session, setSession] = useState({
    phase:       'idle',   // 'idle' | 'uploading' | 'optimizing' | 'done'
    sessionId:   null,
    data:        null,
    uploadError: null,
    aborting:    false,
  });

  const [downloading, setDownloading] = useState({ file: null, zip: false });
  const [recentsKey, setRecentsKey]   = useState(0);

  // True while we're checking sessionStorage for an active session to restore.
  // Prevents the drop zone from rendering before we know if there's an active job.
  const [initializing, setInitializing] = useState(() => !!sessionStorage.getItem(MU_KEY));

  const pollIntervalRef = useRef(null);

  // Keep sessionRef in sync
  useEffect(() => { sessionRef.current = session; }, [session]);

  // ── Restore active session from sessionStorage on mount ──────────────────
  useEffect(() => {
    const savedId = sessionStorage.getItem(MU_KEY);
    if (!savedId) return; // initializing is already false — nothing to check
    authenticatedFetchRef.current(`/api/manual-upload/status/${savedId}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) { sessionStorage.removeItem(MU_KEY); return; }
        const phase = (data.status === 'done' || data.status === 'aborted') ? 'done' : 'optimizing';
        setSession(prev => ({ ...prev, sessionId: savedId, phase, data }));
      })
      .catch(() => sessionStorage.removeItem(MU_KEY))
      .finally(() => setInitializing(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Polling ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const { sessionId, phase } = session;
    if (!sessionId || phase !== 'optimizing') return;

    const poll = async () => {
      try {
        const res  = await authenticatedFetchRef.current(`/api/manual-upload/status/${sessionId}`);
        if (res.status === 404) {
          // The session is gone (server restarted or the 2-hour memory expired): stop polling instead of waiting forever
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          sessionStorage.removeItem(MU_KEY);
          showErrorToast('This upload session is no longer available (the server was restarted). Check Recent Uploads for the finished images and upload the rest again.');
          setSession({ phase: 'idle', sessionId: null, data: null, uploadError: null, aborting: false }); // back to the upload screen, no frozen "Running" card
          setRecentsKey(k => k + 1);
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;

        setSession(prev => ({ ...prev, data }));

        if (data.status === 'done' || data.status === 'aborted') {
          // Check if all files have reached terminal state.
          // After abort, a processing file may still be in-flight — keep polling
          // until it finishes (becomes done/failed/cancelled) so the UI is accurate.
          const allFilesTerminal = (data.files || []).every(f =>
            ['done', 'failed', 'cancelled'].includes(f.status)
          );

          if (!allFilesTerminal) {
            // Files still in-flight — keep polling, don't transition to 'done' yet
            return;
          }

          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;

          // Clear sessionStorage — completed/aborted sessions belong in Recents, not restored on reload
          sessionStorage.removeItem(MU_KEY);

          if (data.status === 'done') {
            const successCount = (data.files || []).filter(f => f.status === 'done').length;
            if (successCount > 0) {
              showSuccessToast(`${successCount} image${successCount !== 1 ? 's' : ''} optimized successfully`);
            }
            setSession(prev => ({ ...prev, phase: 'done' }));
            setRecentsKey(k => k + 1);
          } else {
            setSession(prev => ({ ...prev, phase: 'done' }));
            // Delay recents refresh after abort to let backend persist cancelled records
            setTimeout(() => setRecentsKey(k => k + 1), 1500);
          }
        }
      } catch (err) {
        console.error('[ManualUpload] Polling error:', err);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; } };
  }, [session.sessionId, session.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Upload ────────────────────────────────────────────────────────────────
  const startUpload = useCallback(async (files, optimizationType) => {
    if (!files.length) return;
    setSession(prev => ({ ...prev, phase: 'uploading', uploadError: null }));
    try {
      // Pre-check credits balance
      const balRes  = await authenticatedFetchRef.current('/api/credits/balance');
      const balData = await balRes.json();
      if (balData.success) {
        const remaining = balData.data?.remainingCredits ?? 0;
        if (remaining < files.length) {
          showErrorToast(remaining === 0
            ? `No credits remaining. Please purchase more credits to continue.`
            : `Credit limit reached. You selected ${files.length} images but only have ${remaining} credits. Please select fewer images or purchase more credits.`
          );
          setSession(prev => ({ ...prev, phase: 'idle' }));
          return;
        }
      }

      const appBridge = shopifyAppBridge || window.shopify;
      if (!appBridge) throw new Error('Shopify App Bridge not initialised');
      const sessionToken = await appBridge.idToken();

      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      formData.append('optimizationType', optimizationType);

      const res  = await fetch('/api/manual-upload/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
        body: formData,
      });
      const data = await res.json();
      if (res.status === 402 && data.errorCode === 'INSUFFICIENT_CREDITS') {
        throw new Error('You have no credits remaining. Purchase more credits to continue optimizing images.');
      }
      if (!res.ok || !data.success) throw new Error(data.message || 'Upload failed');

      sessionStorage.setItem(MU_KEY, data.sessionId);
      setSession(prev => ({ ...prev, sessionId: data.sessionId, phase: 'optimizing' }));
      return data;
    } catch (err) {
      showErrorToast(err.message);
      setSession(prev => ({ ...prev, uploadError: err.message, phase: 'idle' }));
    }
  }, []);

  // ── Abort ─────────────────────────────────────────────────────────────────
  const handleAbort = useCallback(async () => {
    const { sessionId, phase, aborting } = sessionRef.current;
    if (!sessionId || phase !== 'optimizing' || aborting) return;

    setSession(prev => ({ ...prev, aborting: true }));
    try {
      const res  = await authenticatedFetchRef.current(`/api/manual-upload/abort/${sessionId}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.message || 'Failed to abort optimization');
        setSession(prev => ({ ...prev, aborting: false }));
      } else {
        // No toast — the "Aborting…" state in the UI is self-explanatory.
        // Do NOT reset aborting — keep it true until polling confirms terminal state.
        // Immediately mark any in-flight/queued files as cancelled so the UI
        // doesn't show partial optimized data for files that were never fully done.
        setSession(prev => ({
          ...prev,
          data: prev.data ? {
            ...prev.data,
            status: 'aborted',
            files: (prev.data.files || []).map(f =>
              f.status === 'processing' || f.status === 'queued'
                ? { ...f, status: 'cancelled', optimizedSizeKb: null, savingsPercent: null }
                : f
            ),
          } : prev.data,
        }));
      }
    } catch (err) {
      showErrorToast(err.message || 'Failed to abort optimization');
      setSession(prev => ({ ...prev, aborting: false }));
    }
  }, []);

  // ── Downloads ─────────────────────────────────────────────────────────────
  const handleDownloadFile = useCallback(async (file) => {
    if (downloading.file) return;
    const { sessionId } = sessionRef.current;
    setDownloading(prev => ({ ...prev, file: file.safeName }));
    try {
      const res  = await authenticatedFetchRef.current(
        `/api/manual-upload/download/${sessionId}/${encodeURIComponent(file.safeName)}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.message || 'Could not generate download link.');
        return;
      }

      // Check content type to see if it's a ZIP or a JSON response with a URL
      const contentType = res.headers.get('Content-Type');
      if (contentType && contentType.includes('application/zip')) {
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        triggerDownload(blobUrl, `${file.safeName}_optimized.${file.ext}`);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const data = await res.json();
        if (!data.success || !data.url) { showErrorToast(data.message || 'Could not generate download link.'); return; }
        triggerDownload(data.url, `${file.safeName}_optimized.${file.ext}`);
      }
    } catch (err) {
      showErrorToast(`Download failed: ${err.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, file: null }));
    }
  }, [downloading.file]);

  const handleDownloadZip = useCallback(async () => {
    if (downloading.zip) return;
    const { sessionId, phase } = sessionRef.current;

    // Pre-download: refresh session status so UI count matches the zip content
    if (phase === 'optimizing' && sessionId) {
      try {
        const res = await authenticatedFetchRef.current(`/api/manual-upload/status/${sessionId}`);
        const data = await res.json();
        if (data.success) setSession(prev => ({ ...prev, data }));
      } catch { /* non-critical */ }
    }

    // Pass the UI-visible done count so backend only zips exactly those files
    const doneCount = (sessionRef.current.data?.files || []).filter(f => f.status === 'done').length;

    setDownloading(prev => ({ ...prev, zip: true }));
    try {
      const zipUrl = doneCount > 0
        ? `/api/manual-upload/download-zip/${sessionId}?maxCount=${doneCount}`
        : `/api/manual-upload/download-zip/${sessionId}`;
      const res  = await authenticatedFetchRef.current(zipUrl);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.message || 'Could not generate ZIP download.');
        return;
      }

      // Check content type
      const contentType = res.headers.get('Content-Type');
      if (contentType && contentType.includes('application/zip')) {
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        triggerDownload(blobUrl, 'optimized_images.zip');
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const data = await res.json();
        if (!data.success || !data.url) { showErrorToast(data.message || 'Could not generate ZIP download.'); return; }
        triggerDownload(data.url, data.filename || 'optimized_images.zip');
      }
    } catch (err) {
      showErrorToast(`ZIP download failed: ${err.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, zip: false }));
    }
  }, [downloading.zip]);

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    sessionStorage.removeItem(MU_KEY);
    setSession({ phase: 'idle', sessionId: null, data: null, uploadError: null, aborting: false });
    setDownloading({ file: null, zip: false });
  }, []);

  return { session, downloading, recentsKey, initializing, startUpload, handleAbort, handleDownloadFile, handleDownloadZip, handleReset };
}

export default useManualUpload;
